import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import logoMenu from "../static/media/logoGRMPNG.webp";

const C = {
  dark: "#0C1F2F",
  navy: "#0057A8",
  navyDark: "#003D73",
  gold: "#FFCD00",
  goldDark: "#E6B800",
  teal: "#5BC8AF",
  gray: "#F5F7FA",
  white: "#ffffff",
};

const SECTIONS = [
  { path: "/", label: "Inicio" },
  { path: "/productos", label: "Productos" },
  { path: "/tienda", label: "Tienda" },
  { path: "/nosotros", label: "Nosotros" },
  { path: "/contacto", label: "Contacto" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const active = location.pathname;

  const navShadow = useMemo(
    () =>
      scrolled
        ? "0 2px 16px rgba(12,31,47,0.10)"
        : "0 1px 0 rgba(12,31,47,0.06)",
    [scrolled],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setOpen(false);
    window.scrollTo(0, 0);
  };

  const linkSx = (isActive) => ({
    px: "16px",
    py: "8px",
    borderRadius: "50px",
    cursor: "pointer",
    fontWeight: isActive ? 700 : 600,
    fontSize: "0.8125rem",
    letterSpacing: "0.01em",
    color: isActive ? C.dark : "#3a4a5c",
    background: isActive ? C.gold : "transparent",
    border: "none",
    transition: "all .22s",
    whiteSpace: "nowrap",
    "&:hover": {
      color: C.dark,
      background: isActive ? C.goldDark : "rgba(255,205,0,0.18)",
    },
  });

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 1200,
        background: C.white,
        boxShadow: navShadow,
        transition: "box-shadow .3s ease",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: "1140px",
          mx: "auto",
          px: { xs: "16px", sm: "24px", md: "28px" },
          height: { xs: "60px", md: "68px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
          boxSizing: "border-box",
        }}
      >
        {/* Logo */}
        <Box
          onClick={() => handleNavigation("/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: { xs: "38px", md: "44px" },
              height: { xs: "38px", md: "44px" },
              borderRadius: "50%",
              border: `2.5px solid ${C.gold}`,
              background: C.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 0 0 4px rgba(255,205,0,0.10)",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={logoMenu}
              alt="Eléctrica GRM"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              sx={{
                width: { xs: "28px", md: "34px" },
                height: { xs: "28px", md: "34px" },
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
            <Typography
              sx={{
                color: C.dark,
                fontWeight: 800,
                fontSize: { xs: "14px", md: "16px" },
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              Eléctrica
            </Typography>
            <Typography
              sx={{
                color: C.navy,
                fontWeight: 700,
                fontSize: "9px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                mt: "2px",
              }}
            >
              GRM
            </Typography>
          </Box>
        </Box>

        {/* Links desktop */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            display: { xs: "none", md: "flex" },
            flex: 1,
            justifyContent: "center",
            mx: "28px",
            maxWidth: "580px",
            background: "rgba(12,31,47,0.04)",
            border: "1px solid rgba(12,31,47,0.07)",
            borderRadius: "50px",
            p: "4px 6px",
          }}
        >
          {SECTIONS.map((s) => (
            <Box
              key={s.path}
              component="button"
              sx={linkSx(active === s.path)}
              onClick={() => handleNavigation(s.path)}
            >
              {s.label}
            </Box>
          ))}
        </Stack>

        {/* CTA desktop */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Box
            component="button"
            onClick={() => handleNavigation("/contacto")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              background: C.navy,
              border: "none",
              borderRadius: "50px",
              px: "20px",
              py: "10px",
              cursor: "pointer",
              transition: "background .2s, transform .15s",
              "&:hover": {
                background: C.navyDark,
                transform: "translateY(-1px)",
              },
            }}
          >
            <PhoneIcon sx={{ color: C.gold, fontSize: 15 }} />
            <Typography
              sx={{
                color: C.white,
                fontWeight: 700,
                fontSize: "12px",
                letterSpacing: "0.06em",
              }}
            >
              COTIZAR
            </Typography>
          </Box>
        </Box>

        {/* Hamburguesa mobile — BLANCO */}
        <Box
          onClick={() => setOpen(true)}
          role="button"
          aria-label="Abrir menú"
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: "6px",
            background: C.white,
            border: `1.5px solid rgba(12,31,47,0.12)`,
            borderRadius: "50px",
            px: "14px",
            py: "7px",
            cursor: "pointer",
            transition: "border-color .2s, background .2s",
            "&:hover": {
              background: C.gray,
              borderColor: "rgba(12,31,47,0.22)",
            },
          }}
        >
          <Typography
            sx={{
              color: C.dark,
              fontWeight: 800,
              fontSize: "11px",
              letterSpacing: "0.08em",
            }}
          >
            MENÚ
          </Typography>
          <MenuIcon sx={{ color: C.dark, fontSize: 17 }} />
        </Box>
      </Box>

      <Box
        sx={{
          height: "3px",
          background: `linear-gradient(90deg, ${C.navy} 0%, ${C.gold} 50%, ${C.teal} 100%)`,
          position: "relative",
          zIndex: 1,
        }}
      />

      {/* Drawer mobile */}
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            width: "82vw",
            maxWidth: 320,
            height: "100%",
            background: `linear-gradient(180deg, ${C.dark} 0%, #050d14 100%)`,
            p: "20px 16px 28px",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "14px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  border: `2px solid ${C.gold}`,
                  background: "rgba(255,255,255,0.04)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 0 0 4px rgba(255,205,0,0.10)",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={logoMenu}
                  alt="Logo"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  sx={{ width: 28, height: 28, objectFit: "contain" }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: C.white,
                    fontWeight: 800,
                    fontSize: "13px",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    lineHeight: 1,
                  }}
                >
                  Eléctrica
                </Typography>
                <Typography
                  sx={{
                    color: C.gold,
                    fontWeight: 700,
                    fontSize: "9px",
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    mt: "1px",
                  }}
                >
                  GRM
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                color: C.white,
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: "50%",
                width: 32,
                height: 32,
                "&:hover": { borderColor: "rgba(255,255,255,0.45)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              height: "1px",
              background: `linear-gradient(90deg, ${C.gold}, rgba(255,205,0,0.03))`,
              mb: "14px",
            }}
          />

          <List sx={{ p: 0, flex: 1 }}>
            {SECTIONS.map((s) => (
              <ListItem key={s.path} disablePadding sx={{ mb: "6px" }}>
                <Box
                  onClick={() => handleNavigation(s.path)}
                  sx={{
                    width: "100%",
                    py: "12px",
                    px: 2.5,
                    borderRadius: "50px",
                    border:
                      active === s.path
                        ? `1px solid ${C.gold}`
                        : "1px solid rgba(255,255,255,0.08)",
                    background:
                      active === s.path ? C.gold : "rgba(255,255,255,0.02)",
                    color:
                      active === s.path ? C.dark : "rgba(255,255,255,0.80)",
                    fontWeight: active === s.path ? 700 : 600,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    transition: "all .2s",
                    textAlign: "center",
                    "&:hover": {
                      borderColor: "rgba(255,205,0,0.35)",
                      background:
                        active === s.path ? C.goldDark : "rgba(255,205,0,0.08)",
                      color: active === s.path ? C.dark : C.gold,
                    },
                  }}
                >
                  {s.label}
                </Box>
              </ListItem>
            ))}
          </List>

          <Box
            component="button"
            onClick={() => handleNavigation("/contacto")}
            sx={{
              mt: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              background: C.gold,
              border: "none",
              borderRadius: "50px",
              py: "13px",
              cursor: "pointer",
              transition: "background .2s",
              "&:hover": { background: C.goldDark },
            }}
          >
            <PhoneIcon sx={{ color: C.dark, fontSize: 16 }} />
            <Typography
              sx={{
                color: C.dark,
                fontWeight: 800,
                fontSize: "13px",
                letterSpacing: "0.06em",
              }}
            >
              COTIZAR AHORA
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
